// Shared constants and utilities
const INVSY_BASE = "/vdr-wpaas/travel-by-investec";
const SPECIAL_SUBDOMAIN_BASE = "/vdr-wpaas/travel-by-investec/flights-cars-hotels";

const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const isInvestecTravelHost = (host) =>
  host === "investectravel.com" ||
  host.endsWith(".investectravel.com") ||
  host === "hotels.secure.investec.com" ||
  host === "localhost" ||
  host === "localhost:8081";

const getSpecialSubdomainPath = (host) => {
  // TEMPORARY: Add localhost for local testing (port 8081 for cars)
  if (host === "localhost" || host === "localhost:8081") return "/cars";

  if (host === "stage--investectravel-flights.netlify.app") return "/flights";
  if (host === "stage--investectravel-cars.netlify.app") return "/cars";
  if (host === "flights.investectravel.com") return "/flights";
  if (host === "cars.investectravel.com") return "/cars";
  if (host === "hotels.secure.investec.com") return "/hotels";
  return null;
};

const buildPath = (url) => {
  const raw =
    ((url && url.pathname) || "/") +
    ((url && url.search) || "") +
    ((url && url.hash) || "");
  return raw.startsWith("/") ? raw.slice(1) : raw;
};

/**
 * Handles navigation within the iframe by calling the appropriate invsy platform method
 * @param {string} targetPath - The path component (e.g., "/flights", "/cars")
 * @param {string} fullPath - The full path with query and hash (e.g., "results?from=JNB")
 * @param {string} source - Source of navigation for logging ("LINK CLICK" or "VUE ROUTER")
 */
const handleIframeNavigation = (targetPath, fullPath, source) => {
  const currentPath = getSpecialSubdomainPath(window.location.hostname);
  const isSameApp = targetPath && currentPath && targetPath === currentPath;

  if (isSameApp) {
    // Same app navigation - use updateUrl
    const updateUrl = window.invsy && window.invsy.platform && window.invsy.platform.updateUrl;
    if (updateUrl) {
      const navPath = `${SPECIAL_SUBDOMAIN_BASE}${targetPath}${fullPath ? `/${fullPath}` : ""}`;
      console.log(`🔗 ${source} - SAME APP - UPDATE URL:`, navPath);
      console.log("📍 Full path:", fullPath);
      updateUrl(navPath);
    } else {
      console.warn("invsy.platform.updateUrl is not available");
    }
  } else {
    // Cross-app or main site navigation - use navigateUrl
    const navigate = 
      (window.invsy && window.invsy.platform && window.invsy.platform.navigateURL) ||
      (window.invsy && window.invsy.platform && window.invsy.platform.navigateUrl);
    if (navigate) {
      let navPath;
      if (targetPath) {
        // Cross-app navigation (e.g., cars to flights)
        navPath = `${SPECIAL_SUBDOMAIN_BASE}${targetPath}${fullPath ? `/${fullPath}` : ""}`;
        console.log(`🔗 ${source} - CROSS APP - NAVIGATE URL:`, navPath);
      } else {
        // Main site navigation
        navPath = fullPath ? `${INVSY_BASE}/${fullPath}` : INVSY_BASE;
        console.log(`🔗 ${source} - MAIN SITE - NAVIGATE URL:`, navPath);
      }
      console.log("📍 Full path:", fullPath);
      navigate(navPath);
    } else {
      console.warn("invsy.platform navigation is not available");
    }
  }
};

/**
 * Sets up automatic iframe navigation handling for investectravel.com links
 *
 * This function intercepts clicks on anchor tags pointing to investectravel.com
 * (and subdomains) and routes them through the invsy platform navigation system
 * instead of allowing default browser navigation that would break out of iframe context.
 *
 * @returns {boolean} - true if handler was set up (running in iframe), false otherwise
 */
function setupIframeNavigationHandler() {
  if (!isInIframe()) {
    console.log("Page is not in an iframe - using standard navigation");
    return false;
  }

  console.log("Page is running within an iframe - setting up custom navigation");

  const toURL = (href) => {
    try {
      return new URL(href, window.location.href);
    } catch {
      return null;
    }
  };

  const isSkippableProtocol = (url) =>
    url && ["mailto:", "tel:", "javascript:", "data:"].includes(url.protocol);

  // One listener handles current and future links.
  const onClick = (e) => {
    // Find nearest <a href> (covers icons/spans inside anchors)
    const a = e.target.closest && e.target.closest("a[href]");
    if (!a) return;

    // Opt-out hook if you need it on specific links
    if (a.hasAttribute("data-no-iframe-nav")) return;

    const url = toURL(a.getAttribute("href"));
    if (!url || isSkippableProtocol(url)) return;

    console.log("🔍 Link clicked - hostname:", url.hostname, "pathname:", url.pathname);

    // Only hijack investectravel.com (and subdomains)
    if (!isInvestecTravelHost(url.hostname)) {
      console.log("❌ Hostname not recognized:", url.hostname);
      return;
    }

    e.preventDefault(); // prevent default navigation

    // Determine target app and extract path
    const targetPath = getSpecialSubdomainPath(url.hostname);
    const fullPath = buildPath(url);

    console.log("🎯 Target path:", targetPath, "Full path:", fullPath);

    // Use shared navigation handler
    handleIframeNavigation(targetPath, fullPath, "LINK CLICK");
  };

  // Handle initial URL update on page load
  const updateInitialUrl = () => {
    const updateUrl = window.invsy && window.invsy.platform && window.invsy.platform.updateUrl;
    if (updateUrl) {
      const currentHost = window.location.hostname;
      const specialPath = getSpecialSubdomainPath(currentHost);

      if (specialPath) {
        // Special subdomain - set initial URL without additional path
        const navPath = `${SPECIAL_SUBDOMAIN_BASE}${specialPath}`;
        console.log("Handler: Setting initial URL for special subdomain:", navPath);
        updateUrl(navPath);
      }
    }
  };

  // Attach once; delegation means no MutationObserver needed
  document.addEventListener("click", onClick, { capture: true });
  
  // Set initial URL
  updateInitialUrl();
  
  return true;
}

/**
 * Sets up Vue Router integration for iframe navigation
 * Call this from main.js after creating the router instance
 *
 * @param {VueRouter} router - The Vue Router instance
 */
export function setupVueRouterIntegration(router) {
  if (!isInIframe()) {
    console.log("Vue Router integration: Not in iframe, skipping");
    return;
  }

  // Add afterEach navigation guard to update URL after route changes
  router.afterEach((to, from) => {
    const currentHost = window.location.hostname;
    const targetPath = getSpecialSubdomainPath(currentHost);

    if (targetPath) {
      // Build the full path including query params and hash
      const fullPath = to.fullPath.startsWith("/") ? to.fullPath.slice(1) : to.fullPath;

      console.log("🔄 Vue Router Navigation - from:", from.path, "to:", to.path);

      // Use shared navigation handler
      handleIframeNavigation(targetPath, fullPath, "VUE ROUTER");
    }
  });

  console.log("✅ Vue Router integration: Navigation guard added");
}

export default setupIframeNavigationHandler;